/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { alpha, createTheme, PaletteOptions, ThemeOptions } from '@mui/material/styles';
import { ColorPartial, PaletteColor } from '@mui/material/styles/createPalette';
import { CSSProperties } from 'react';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    body3: CSSProperties;
    h7: CSSProperties;
    h8: CSSProperties;
    buttonText2: CSSProperties;
    caption: CSSProperties;
  }

  interface TypographyVariantsOptions {
    body3?: CSSProperties;
    h7?: CSSProperties;
    h8?: CSSProperties;
    buttonText2?: CSSProperties;
    caption?: CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body3: true;
    h7: true;
    h8: true;
    buttonText2: true;
    caption: true;
  }
}

declare module '@mui/material/styles/createPalette' {
  interface Palette {
    surface: ColorPartial;
  }
  interface PaletteOptions {
    surface?: ColorPartial;
  }
  interface TypeText extends ColorPartial {
    primary: string;
    secondary: string;
    disabled: string;
  }
}

export const yellow = {
  100: '#FEF8EA',
  200: '#FDEDCC',
  500: '#F7B731',
  600: '#DEA52C'
};

export const orange = {
  100: '#FFF3E9',
  500: '#FF8923'
};

const neutral = {
  5: '#D9D9D9'
};

const grey = {
  400: '#D6DCE2'
};

export const purple = {
  100: '#F3EEFA',
  500: '#8854D0'
};

export const white = {
  400: '#F9FBFD'
};

export const palette = {
  mode: 'light',
  primary: {
    main: '#F3A937',
    light: '#F7C87F',
    dark: '#D1840D',
    contrastText: '#FFFFFF',
    bg: '#F3A93729'
  },
  secondary: {
    main: '#F3A937'
  },
  error: {
    light: '#F9D1D5',
    main: '#D71816'
  },
  surface: {
    50: '#FAFAFA',
    100: '#F2F2F2',
    200: '#ECECEC',
    300: '#B1B1B1',
    400: '#777777',
    500: '#3D3D3D',
    600: '#373737',
    700: '#777777',
    800: '#777777',
    900: '#777777'
  },
  text: {
    50: '#F6F7F7',
    100: '#ECEEF0',
    200: '#D0D5D9',
    300: '#B4BBC1',
    400: '#7B8893',
    500: '#435565',
    600: '#3C4D5B',
    700: '#28333D',
    800: '#1E262D',
    900: '#141A1E'
  },
  background: {
    default: '#F7F7F7',
    paper: '#FFFFFF'
  }
};

const themeOptions: ThemeOptions = {
  palette: {
    ...(palette as PaletteOptions)
  },
  typography: {
    // typography variants are named according to Figma for easier usage
    fontFamily: ['Open Sans', 'sans-serif'].join(','),
    fontWeightBold: 600,
    h3: {
      fontSize: '2rem',
      lineHeight: 1.1,
      letterSpacing: '0.25px'
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.5
    },
    h5: {
      fontWeight: 700,
      fontSize: '1.4375rem',
      lineHeight: 1.5,
      letterSpacing: '0.15px'
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.1875rem',
      lineHeight: 1.5
    },
    h7: {
      fontWeight: 600,
      fontSize: '0.9375rem',
      lineHeight: 1.5
    },
    h8: {
      fontWeight: 600,
      fontSize: '0.8125rem',
      lineHeight: 1.5
    },
    body1: {
      fontSize: '0.9375rem',
      lineHeight: 1.5,
      letterSpacing: '0.5px'
    },
    body2: {
      lineHeight: 1.5
    },
    body3: {
      fontSize: '0.75rem',
      lineHeight: 1.5
    },
    buttonText2: {
      lineHeight: 1.5,
      fontWeight: 600,
      fontSize: '0.8125rem',
      letterSpacing: '0.5px'
    },
    caption: {
      lineHeight: 1.5,
      fontSize: '0.75rem',
      letterSpacing: '0.4px'
    }
  },
  shape: {
    borderRadius: 4
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600
        },
        sizeLarge: {
          paddingTop: '11px',
          paddingBottom: '11px',
          width: '412px',
          height: '3rem'
        },
        sizeSmall: {
          fontSize: '0.82rem',
          lineHeight: 1.5,
          padding: '0.5rem 1rem'
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          marginLeft: 'auto',
          marginRight: 'auto'
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '2rem 2rem 1rem'
        }
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '2rem 2rem .5rem'
        }
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '2rem',
          '& > :not(:first-of-type)': {
            marginLeft: '1rem'
          }
        }
      }
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          border: `solid 1px ${palette.text![100]}`,
          borderRadius: '8px',
          '&::after': {
            left: '8px',
            bottom: '-1px',
            right: '8px'
          },
          '&::before': {
            left: '8px',
            bottom: '-1px',
            right: '8px',
            borderWidth: '0 !important'
          },
          '&:hover::before': {
            borderWidth: '1px !important',
            borderColor: 'black'
          },
          '&:hover': {
            backgroundColor: 'transparent'
          },
          '&.Mui-focused': {
            backgroundColor: 'transparent'
          },
          '&.Mui-disabled': {
            backgroundColor: 'transparent',
            '&::before': {
              borderColor: 'transparent'
            }
          }
        },
        input: {
          padding: '22px 8px 5px'
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: palette.text![400],
          fontSize: '14px',
          fontWeight: '500',
          transform: 'translate(0, 0)'
        },
        filled: {
          color: palette.text![300],
          transform: 'translate(12px,14px) scale(1)',
          '&.Mui-focused': {
            transform: 'translate(10px,7px) scale(0.75)'
          },
          '&.MuiInputLabel-shrink': {
            transform: 'translate(10px,7px) scale(0.75)'
          }
        },
        asterisk: {
          color: (palette.error as PaletteColor).main
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          '&.Mui-error': {
            border: '1px solid red'
          }
        },
        filled: {
          '&:focus': {
            backgroundColor: 'transparent'
          },
          '&.Mui-focused': {
            backgroundColor: 'transparent'
          }
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: palette.text![500],
          fontWeight: 400,
          fontSize: '.75rem',
          lineHeight: 1.5,
          marginTop: '.5rem'
        }
      }
    },
    MuiLink: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          letterSpacing: '0.5px',
          textDecoration: 'none',
          '&:visited': {
            color: (palette.primary as PaletteColor).main
          }
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: '10px'
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          padding: '1rem',
          color: palette.surface![500],
          alignItems: 'center'
        },
        standardWarning: {
          backgroundColor: yellow[200]
        },
        standardSuccess: {
          backgroundColor: (palette.secondary as ColorPartial)![200]
        },
        standardError: {
          backgroundColor: (palette.error as ColorPartial)![200]
        },
        message: {
          padding: 0
        },
        icon: {
          padding: 0,
          marginRight: '20px',
          marginLeft: '4px'
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&:not(.Mui-checked) > svg': {
            color: neutral[5]
          }
        }
      }
    },
    MuiPagination: {
      styleOverrides: {
        text: {
          '.MuiPaginationItem-root': {
            fontWeight: 600,
            fontSize: '0.8125rem',
            lineHeight: 1.5,
            letterSpacing: '0.5px',
            color: palette.text![400],
            margin: 0,
            padding: 0
          },
          '.Mui-selected': {
            backgroundColor: 'transparent !important'
          },
          '.Mui-selected.MuiPaginationItem-textSecondary': {
            color: (palette.secondary as PaletteColor).main
          }
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: '0.4rem 1.2rem 0.4rem 0.5rem',
          '&:not(.Mui-checked) svg': {
            color: grey[400]
          },
          '& svg.MuiSvgIcon-fontSizeSmall': {
            fontSize: '1rem'
          }
        }
      }
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          width: '30%',
          '&.MuiFormControl-fullWidth': {
            width: '100%'
          }
        }
      }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          color: palette.surface![900],
          fontSize: '0.9375rem',
          lineHeight: 1.5,
          letterSpacing: '0.5px'
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        arrow: {
          color: '#323232'
        },
        tooltip: {
          backgroundColor: '#323232',
          fontWeight: 600,
          fontSize: '0.75rem',
          lineHeight: 1.5,
          letterSpacing: '0.4px',
          padding: '1rem',
          maxWidth: '250px'
        },
        tooltipPlacementTop: {
          textAlign: 'center',
          maxWidth: '300px'
        },
        tooltipPlacementBottom: {
          textAlign: 'center'
        },
        tooltipPlacementRight: {
          textAlign: 'left'
        }
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          border: `1px solid ${palette.text![100]}`,
          borderRadius: '4px',
          boxShadow: 'none',
          margin: '1rem 0',
          '&:before': {
            display: 'none'
          }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily: ['Open Sans', 'sans-serif'].join(','),
          'label + &': {
            marginTop: 8
          },
          '&.Mui-error > input': {
            border: '1px solid #D71816'
          },
          '& .MuiInputBase-input': {
            borderRadius: 4,
            position: 'relative',
            backgroundColor: 'white',
            border: '1px solid #ced4da',
            fontSize: 14,
            padding: '10px 12px',
            '&:focus': {
              boxShadow: `${alpha(palette.primary.main, 0.25)} 0 0 0 0.2rem`,
              borderColor: palette.primary.main
            }
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          border: '1px solid #e0e0e0',
          boxShadow: 'none'
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected.Mui-disabled': {
            backgroundColor: 'transparent'
          }
        }
      }
    }
  }
};

export const theme = createTheme(themeOptions);
